import { ReactNode, Suspense } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay"
import { MagicWand } from "@phosphor-icons/react"
import { LoadingSpinner } from "@/components/loading-spinner"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import {
  chEpisodeOfCareAiSuggestionsQuery,
  chEpisodeOfCareAiSuggestionsQuery$data,
} from "@/modules/patients/detail/episode-of-care/services/ch/__generated__/chEpisodeOfCareAiSuggestionsQuery.graphql"
import { chAiSuggestionsQueryDef } from "@/modules/patients/detail/episode-of-care/services/ch/chEpisodeOfCareAiSuggestionsQuery"
import { deEpisodeOfCareAiSuggestionsQuery } from "@/modules/patients/detail/episode-of-care/services/de/__generated__/deEpisodeOfCareAiSuggestionsQuery.graphql"
import { deAiSuggestionsQueryDef } from "@/modules/patients/detail/episode-of-care/services/de/deEpisodeOfCareAiSuggestionsQuery"
import { AssignEpisodeOfCareFormType } from "@/modules/patients/detail/progress-note/progress-note-card-header"
import { useEpisodeOfCareStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { createOrReuseEpisodeOfCare } from "@/modules/patients/services/createOrReuseEpisodeOfCare"
import { useUpdateProgressNoteEpisodeOfCareMutation } from "@/modules/patients/services/useUpdateProgressNoteEpisodeOfCareMutation"
import { Country } from "@/types/__generated__/union-types"

type SuggestionDiagnosis = chEpisodeOfCareAiSuggestionsQuery$data["suggestedEpisodeOfCareIcdDiagnoses"][number]

const QuestionnaireSuggestionIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.9319 15.4806V9.79957C14.9319 7.49397 13.4226 6 11.1287 6C9.75997 6 8.75406 6.49804 8.1302 7.328C7.56655 6.47959 6.64107 6 5.39342 6C4.10549 6 3.15944 6.46115 2.59614 7.2358V6.25817H0V15.4806H2.59614V10.2976C2.59614 8.91416 3.38089 8.21344 4.48758 8.21344C5.57431 8.21344 6.1782 8.87727 6.1782 9.96566V15.4806H8.77402V10.2976C8.77402 8.91416 9.49859 8.21344 10.6455 8.21344C11.7322 8.21344 12.3358 8.87727 12.3358 9.96566V15.4806H14.9319ZM15.4667 16.7482C15.4667 16.0568 16.0583 15.4963 16.7882 15.4963H22.6785C23.4084 15.4963 24 16.0568 24 16.7482C24 17.4396 23.4084 18 22.6785 18H16.7882C16.0583 18 15.4667 17.4396 15.4667 16.7482Z"
      className="fill-medgate-blue"
    />
  </svg>
)

interface BaseEpisodeOfCareProps {
  country: Country
  progressNoteId: string
  episodeOfCareId: string
}

interface EpisodeOfCareSofiaSuggestionsProps extends BaseEpisodeOfCareProps {
  appointmentId?: string
  questionnaireSuggestions: ReadonlyArray<SuggestionDiagnosis>
}

const useHandleSelectSuggestion = (country: Country, progressNoteId: string, episodeOfCareId: string) => {
  const { setValue } = useFormContext<AssignEpisodeOfCareFormType>()
  const patientId = usePatientParam()
  const setProgressNoteId = useEpisodeOfCareStoreContext((state) => state.setProgressNoteId)
  const setSelectedEpisodeOfCare = useEpisodeOfCareStoreContext((state) => state.setEpisodeOfCareFragmentReference)
  const updateProgressNoteEpisodeOfCare = useUpdateProgressNoteEpisodeOfCareMutation(country)

  return (diagnosisCode: string) => {
    createOrReuseEpisodeOfCare(country, { diagnosisCode, patientId, relatedConditions: [] }, (episodeOfCareRes) => {
      if (!episodeOfCareRes) return

      updateProgressNoteEpisodeOfCare(
        { id: progressNoteId, episodeOfCareId: episodeOfCareRes.id },
        episodeOfCareId,
        () => {
          setValue("icdCode", diagnosisCode, { shouldValidate: true })
          setSelectedEpisodeOfCare(episodeOfCareRes)
        },
      )
    })
    setProgressNoteId(null)
  }
}

interface SuggestionItemProps {
  icon?: ReactNode
  suggestion: SuggestionDiagnosis
  onSelectSuggestion: (diagnosisCode: string) => void
}

const SuggestionItem = ({ icon, suggestion, onSelectSuggestion }: SuggestionItemProps) => (
  <DropdownMenuItem
    key={suggestion.id}
    className="h-auto p-0"
    onSelect={() => onSelectSuggestion(suggestion.diagnosisCode)}>
    <div className={cn("hover:bg-surface-hovered w-full rounded-md p-2")}>
      <div className="flex gap-2">
        {icon}
        <span className="truncate text-sm font-normal">{suggestion.text}</span>
      </div>
    </div>
  </DropdownMenuItem>
)

const AISuggestionsItems = ({
  suggestions,
  hasQuestionnaireSuggestions,
  onSelectSuggestion,
}: {
  suggestions: ReadonlyArray<SuggestionDiagnosis>
  hasQuestionnaireSuggestions: boolean
  onSelectSuggestion: (diagnosisCode: string) => void
}) => {
  const { t } = useTranslation()

  if (suggestions.length === 0 && !hasQuestionnaireSuggestions) {
    return (
      <span className="text-on-surface items-center truncate p-2 px-2 text-sm leading-5">
        {t("episodeOfCare.progressNote.noSofiaSuggestions")}
      </span>
    )
  }

  return (
    <>
      {suggestions.map((s) => (
        <SuggestionItem
          key={s.id}
          icon={<MagicWand className="text-on-plum-surface" />}
          suggestion={s}
          onSelectSuggestion={onSelectSuggestion}
        />
      ))}
    </>
  )
}

const ChAISuggestionsLoader = (
  props: BaseEpisodeOfCareProps & { appointmentId: string; hasQuestionnaireSuggestions: boolean },
) => {
  const { country, progressNoteId, episodeOfCareId, hasQuestionnaireSuggestions } = props
  const [queryReference, loadQuery] = useQueryLoader<chEpisodeOfCareAiSuggestionsQuery>(chAiSuggestionsQueryDef)
  const handleSelectSuggestion = useHandleSelectSuggestion(country, progressNoteId, episodeOfCareId)

  if (!queryReference && props.appointmentId)
    loadQuery({ appointmentId: props.appointmentId }, { fetchPolicy: "network-only" })

  return (
    <Suspense fallback={<LoadingSpinner isFullPage size={24} className="p-4" />}>
      {queryReference ? (
        <ChAISuggestionsContent
          queryRef={queryReference}
          hasQuestionnaireSuggestions={hasQuestionnaireSuggestions}
          onSelectSuggestion={handleSelectSuggestion}
        />
      ) : null}
    </Suspense>
  )
}

const ChAISuggestionsContent = ({
  queryRef,
  hasQuestionnaireSuggestions,
  onSelectSuggestion,
}: {
  queryRef: PreloadedQuery<chEpisodeOfCareAiSuggestionsQuery>
  hasQuestionnaireSuggestions: boolean
  onSelectSuggestion: (diagnosisCode: string) => void
}) => {
  const { suggestedEpisodeOfCareIcdDiagnoses } = usePreloadedQuery<chEpisodeOfCareAiSuggestionsQuery>(
    chAiSuggestionsQueryDef,
    queryRef,
  )
  return (
    <AISuggestionsItems
      suggestions={suggestedEpisodeOfCareIcdDiagnoses}
      hasQuestionnaireSuggestions={hasQuestionnaireSuggestions}
      onSelectSuggestion={onSelectSuggestion}
    />
  )
}

const DeAISuggestionsLoader = (
  props: BaseEpisodeOfCareProps & { appointmentId: string; hasQuestionnaireSuggestions: boolean },
) => {
  const { country, progressNoteId, episodeOfCareId, hasQuestionnaireSuggestions } = props
  const [queryReference, loadQuery] = useQueryLoader<deEpisodeOfCareAiSuggestionsQuery>(deAiSuggestionsQueryDef)
  const handleSelectSuggestion = useHandleSelectSuggestion(country, progressNoteId, episodeOfCareId)

  if (!queryReference && props.appointmentId)
    loadQuery({ appointmentId: props.appointmentId }, { fetchPolicy: "network-only" })

  return (
    <Suspense fallback={<LoadingSpinner isFullPage size={24} className="p-4" />}>
      {queryReference ? (
        <DeAISuggestionsContent
          queryRef={queryReference}
          hasQuestionnaireSuggestions={hasQuestionnaireSuggestions}
          onSelectSuggestion={handleSelectSuggestion}
        />
      ) : null}
    </Suspense>
  )
}

const DeAISuggestionsContent = ({
  queryRef,
  hasQuestionnaireSuggestions,
  onSelectSuggestion,
}: {
  queryRef: PreloadedQuery<deEpisodeOfCareAiSuggestionsQuery>
  hasQuestionnaireSuggestions: boolean
  onSelectSuggestion: (diagnosisCode: string) => void
}) => {
  const { suggestedEpisodeOfCareIcdDiagnoses } = usePreloadedQuery<deEpisodeOfCareAiSuggestionsQuery>(
    deAiSuggestionsQueryDef,
    queryRef,
  )
  return (
    <AISuggestionsItems
      suggestions={suggestedEpisodeOfCareIcdDiagnoses}
      hasQuestionnaireSuggestions={hasQuestionnaireSuggestions}
      onSelectSuggestion={onSelectSuggestion}
    />
  )
}

export const EpisodeOfCareSofiaSuggestions = ({
  country,
  progressNoteId,
  episodeOfCareId,
  appointmentId,
  questionnaireSuggestions,
}: EpisodeOfCareSofiaSuggestionsProps) => {
  const { t } = useTranslation()
  const handleSelectSuggestion = useHandleSelectSuggestion(country, progressNoteId, episodeOfCareId)

  const hasQuestionnaireSuggestions = questionnaireSuggestions.length > 0
  const loaderProps = { country, progressNoteId, episodeOfCareId, hasQuestionnaireSuggestions }

  return (
    <>
      <DropdownMenuSeparator className="border-border border-t" />
      <div className="text-on-surface flex w-full flex-col px-2 py-1" onClick={(e) => e.stopPropagation()}>
        <div className="text-on-surface-subdued px-2 text-sm leading-5 font-medium">
          {t("episodeOfCare.progressNote.sofiaSupportedSuggestions")}
        </div>
        <div className="flex flex-col py-2">
          {appointmentId &&
            (country === "DE" ? (
              <DeAISuggestionsLoader appointmentId={appointmentId} {...loaderProps} />
            ) : (
              <ChAISuggestionsLoader appointmentId={appointmentId} {...loaderProps} />
            ))}
          {questionnaireSuggestions.map((s) => (
            <SuggestionItem
              key={s.id}
              icon={<QuestionnaireSuggestionIcon />}
              suggestion={s}
              onSelectSuggestion={handleSelectSuggestion}
            />
          ))}
          {!appointmentId && !hasQuestionnaireSuggestions && (
            <span className="text-on-surface items-center truncate p-2 px-2 text-sm leading-5">
              {t("episodeOfCare.progressNote.noSofiaSuggestions")}
            </span>
          )}
        </div>
      </div>
    </>
  )
}
